import { useTranslation } from 'react-i18next';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { Avatar, TableCell } from '@mui/material';
import { ChevronIcon } from '@icons/ChevronIcon';
import type { ProductSubscriptionListModel } from '@repositories/productSubscriptionRepository/models/ProductSubscriptionListModel';
import { dateFormat } from '@utils/dateUtil';
import { numberFormat } from '@utils/numberFormat';

type Props = {
  product: ProductSubscriptionListModel;
  isExpanded: boolean;
  onToggle: () => void;
};

export const SubscriptionGroupHeader = ({ product, isExpanded, onToggle }: Props) => {
  const { t } = useTranslation();
  const { colors } = useTheme();

  const planSummary =
    product.planCount > 1
      ? `${product.representativePlanName} ${t('Subscrib_Main_PlanCountMore', { count: product.planCount - 1 })}`
      : product.representativePlanName || '-';

  const userGroupSummary =
    product.userGroupCount > 1
      ? `${product.representativeUserGroupName} ${t('Subscrib_Main_PlanCountMore', {
          count: product.userGroupCount - 1,
        })}`
      : product.representativeUserGroupName || '-';

  const nextExpiryDisplay =
    product.nextExpiryDate === null ? '-' : dateFormat(product.nextExpiryDate, '-', t('DateFormat_YMD'));

  // 그룹 헤더는 앱(software) 단위 합계, 하위 구독 행은 각 구독의 billingStat을 표시.
  const annualCostDisplay = numberFormat({ num: product.annualPaymentEstimate ?? undefined });

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggle();
  };

  return (
    <>
      <GroupCell onClick={handleClick}>
        <GroupNameWrapper>
          <ExpandButton
            type="button"
            aria-expanded={isExpanded}
            aria-label={t(isExpanded ? 'Subscrib_Main_Aria_Collapse' : 'Subscrib_Main_Aria_Expand')}
          >
            <ChevronIcon width={16} height={16} rotateNum={isExpanded ? 180 : 90} color={colors['ic-gray-main']} />
          </ExpandButton>
          <Avatar
            sx={{ width: 20, height: 20, borderRadius: '4px', marginRight: '8px' }}
            src={product.softwareIconUrl}
          />
          <SoftwareName>{product.softwareName}</SoftwareName>
          <SubscriptionCountBadge>
            {product.totalSubscriptionCount} {t('Subscrib_Main_02')}
          </SubscriptionCountBadge>
        </GroupNameWrapper>
      </GroupCell>
      <GroupCell onClick={handleClick}>{planSummary}</GroupCell>
      <GroupCell align="right" onClick={handleClick}>
        {annualCostDisplay}
      </GroupCell>
      <GroupCell onClick={handleClick}>{nextExpiryDisplay}</GroupCell>
      <GroupCell onClick={handleClick}>-</GroupCell>
      <GroupCell onClick={handleClick}>-</GroupCell>
      <GroupCell onClick={handleClick}>{userGroupSummary}</GroupCell>
      <GroupCell onClick={handleClick} />
    </>
  );
};

const GroupCell = styled(TableCell)`
  padding: 12px 16px;
  background-color: inherit;
  border-bottom: 1px solid ${({ theme }) => theme.colors['border-gray-lighter']};
  font-weight: 600;
  cursor: pointer;
`;

const GroupNameWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const ExpandButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  margin-right: 8px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  transition: transform 0.2s ease;
`;

const SoftwareName = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.colors['text-gray-main']};
`;

const SubscriptionCountBadge = styled.span`
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 12px;
  background-color: ${({ theme }) => theme.colors['bg-primary-lighter']};
  color: ${({ theme }) => theme.colors['text-primary']};
  font-size: 12px;
  font-weight: 500;
`;

